import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loading from 'assets/images/loading.png';
import CommonPdfViewer from 'component/Common/CommonPdfViewer';

const DocumentView = ({ open, onClose, fileUrl, fileName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (open && fileUrl) {
      setPdfLoading(true);
      setPdfUrl(fileUrl);
    } else {
      setPdfUrl(null);
      setPdfLoading(false);
    }
  }, [open, fileUrl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: styles.dialogPaper,
      }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.title}>
          {fileName || 'Document Viewer'}
        </Typography>

        <IconButton onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        {pdfUrl ? (
          <Box sx={styles.pdfContainer}>
            <CommonPdfViewer
              fileUrl={pdfUrl}
              defaultScale={isMobile ? 0.55 : 1.5}
              onDocumentLoad={({ doc }) => {
                console.log('PDF loaded:', doc.numPages, 'page(s)');
                setPdfLoading(false);
              }}
              onDocumentLoadError={(error) => {
                console.error('Failed to load PDF:', error);
                setPdfLoading(false);
              }}
            />

            {pdfLoading && (
              <Box sx={styles.loadingOverlay}>
                <img
                  alt="Loading"
                  src={Loading}
                  className="loaderImage"
                  style={styles.loaderImage}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={styles.noPdfContainer}>
            <Typography sx={styles.noPdfText}>No PDF file available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  dialogPaper: {
    width: {
      xs: '100%',
      sm: '90%',
      md: '80%',
    },

    maxWidth: {
      xs: '100%',
      sm: '1000px',
      md: '1200px',
    },

    height: {
      xs: '100%',
      sm: '100vh',
    },

    maxHeight: {
      xs: '100%',
      sm: '100vh',
    },

    margin: 0,

    borderRadius: {
      xs: 0,
      sm: '12px',
    },

    overflow: 'hidden',

    display: 'flex',
    flexDirection: 'column',
  },

  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    padding: {
      xs: '12px 16px',
      sm: '16px 24px',
    },

    minHeight: {
      xs: '56px',
      sm: '64px',
    },

    boxSizing: 'border-box',

    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#F9FAFB',

    flexShrink: 0,
  },

  title: {
    fontFamily: 'Poppins',
    fontWeight: 600,

    fontSize: {
      xs: '14px',
      sm: '18px',
    },

    color: '#1E1E1E',

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    maxWidth: 'calc(100% - 50px)',
  },

  closeButton: {
    flexShrink: 0,
  },

  dialogContent: {
    padding: 0,
    margin: 0,

    backgroundColor: '#F5F5F5',

    flex: 1,
    minHeight: 0,

    overflow: 'hidden',

    '&.MuiDialogContent-root': {
      padding: 0,
    },
  },

  loadingContainer: {
    width: '100%',
    height: '100%',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    backgroundColor: '#fff',
  },

  loaderImage: {
    height: '75px',
    width: '75px',
  },

  pdfContainer: {
    position: 'relative',

    width: '100%',
    height: '100%',

    overflowY: 'auto',
    overflowX: 'hidden',

    backgroundColor: '#F5F5F5',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',

    boxSizing: 'border-box',

    padding: {
      xs: '10px 0',
      sm: '20px 0',
    },

    // keep the rest of your existing styles
  },
  noPdfContainer: {
    width: '100%',
    height: '100%',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    backgroundColor: '#fff',
  },

  noPdfText: {
    fontFamily: 'Poppins',
    color: '#666666',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    backgroundColor: '#fff',

    zIndex: 10,
  },
};

export default DocumentView;
